/**
 * <ai-product-form> owns PDP form submission and feedback.
 */
if (!customElements.get("ai-product-form")) {
  customElements.define(
    "ai-product-form",
    class AiProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector("form");
        if (!this.form) return;
        this._productDetail = this.closest("ai-product-detail");
        this.submitButton = Array.from(this.form.elements).find(element => element.matches("[data-add-to-cart]"));
        if (!this.submitButton) return;
        this.cart =
          document.querySelector("ai-cart-notification") ||
          document.querySelector("ai-cart-drawer");
        this.hideErrors = this.dataset.hideErrors === "true";
        this._boundSubmit ||= this._onSubmit.bind(this);
        this._boundQuantityChange ||= this._onQuantityChange.bind(this);
        this._boundRecipientInvalid ||= (event) => event.preventDefault();
        this._buttonObserver ||= new MutationObserver(records => {
          if (!this._submitting || !records.length) return;
          this._purchaseDisabled = this._buttonDisabled();
          this._applyButtonDisabled();
        });
        this.form.addEventListener("submit", this._boundSubmit);
        this._productDetail?.addEventListener("quantity:change", this._boundQuantityChange);
        this._preventNativeRecipientInvalid();
        if (document.querySelector("ai-cart-drawer") || document.querySelector("cart-drawer")) {
          this.submitButton?.setAttribute("aria-haspopup", "dialog");
        }
      }

      disconnectedCallback() {
        this.form?.removeEventListener("submit", this._boundSubmit);
        this._productDetail?.removeEventListener("quantity:change", this._boundQuantityChange);
        this.form?.querySelector('[name="recipientEmail"]')?.removeEventListener("invalid", this._boundRecipientInvalid);
        this._requestController?.abort();
        this._requestController = null;
        if (this._submitting) this._setLoading(false);
        this._buttonObserver?.disconnect();
        window.clearTimeout(this._successTimer);
        this.submitButton?.removeAttribute("data-added");
      }

      async _onSubmit(event) {
        event.preventDefault();
        if (!this.form || this._submitting || this._buttonDisabled()) return;

        const validation = this._validateGiftCardRecipient(this.form);
        if (!validation.valid) {
          this._showRecipientError(validation.message);
          return;
        }

        const product = this._productDetail || this;
        const variantInput = product.querySelector("[data-product-variant-input]");
        const quantityInput = product.querySelector("[data-product-quantity-input]");
        // Quantity and purchase blocks can live in separate layout containers.
        // Read their semantic controls at submission, preserving other form fields.
        quantityInput?.dispatchEvent(new Event("change", { bubbles: true }));
        if (!this.form.reportValidity() || (quantityInput && !quantityInput.reportValidity())) return;
        const formData = new FormData(this.form);
        const variantId = variantInput ? variantInput.value : formData.get("id");
        const quantity = Number(quantityInput ? quantityInput.value : formData.get("quantity") ?? 1);
        if (!variantId) {
          this.handleErrorMessage(this.submitButton.dataset.unavailableText || "Select a variant");
          return;
        }
        if (!Number.isSafeInteger(quantity) || quantity < 1) return;
        formData.set("id", variantId);
        formData.set("quantity", String(quantity));
        this.handleErrorMessage();
        this._setStatus("");
        this.submitButton.removeAttribute("data-added");
        window.clearTimeout(this._successTimer);
        this._setLoading(true);
        const requestController = new AbortController();
        this._requestController = requestController;

        try {
          const sections = this._getSectionsToRequest();
          if (sections.length) {
            formData.set("sections", sections.map((section) => section.section || section.id).join(","));
            formData.set("sections_url", window.location.pathname);
          }
          this.cart?.setActiveElement?.(document.activeElement);

          const response = await fetch(window.routes?.cart_add_url || "/cart/add.js", {
            method: "POST",
            headers: {
              "X-Requested-With": "XMLHttpRequest",
              Accept: "application/json",
            },
            body: formData,
            signal: requestController.signal,
          });
          const payload = await response.json();
          if (requestController.signal.aborted) return;

          if (!response.ok || payload.status) {
            const error = new Error(payload.description || payload.message || "Add to cart failed");
            error.payload = payload;
            throw error;
          }

          this._setSuccess();
          this._publishCartUpdate(payload, variantId);
          this._renderCartContents(payload);
          this.dispatchEvent(new CustomEvent("product-form:add-success", { bubbles: true, detail: { cartData: payload } }));
        } catch (error) {
          if (requestController.signal.aborted) return;
          const payload = error.payload || { message: error.message, description: error.message };
          this._notifyCartErrorToast(payload);
          this.handleErrorMessage(payload.description || payload.message || error.message);
          this._publishCartError(payload, variantId);
          this.dispatchEvent(new CustomEvent("product-form:add-error", { bubbles: true, detail: { error: payload } }));
        } finally {
          if (this._requestController === requestController) {
            this._setLoading(false);
            this._requestController = null;
          }
        }
      }

      _onQuantityChange(event) {
        const input = this.form?.querySelector("[data-product-quantity-input]");
        if (input && Number.isSafeInteger(event.detail?.quantity)) input.value = String(event.detail.quantity);
      }

      _validateGiftCardRecipient(form) {
        const enabled = form.querySelector('[name="send_gift_card_to_recipient"]')?.checked;
        const email = form.querySelector('[name="recipientEmail"]');
        if (!enabled || !email) return { valid: true };
        const value = email.value.trim();
        if (!value) {
          return {
            valid: false,
            message: form.querySelector('[name="emailRequired"]')?.value || "Recipient email is required.",
          };
        }
        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        email.toggleAttribute("aria-invalid", !valid);
        return {
          valid,
          message: valid ? "" : form.querySelector('[name="emailFormat"]')?.value || "Enter a valid email address.",
        };
      }

      _showRecipientError(message) {
        const error = this.form?.querySelector("[data-recipient-email-error]");
        if (!error) {
          this.handleErrorMessage(message);
          return;
        }
        error.textContent = message || "";
        error.hidden = !message;
      }

      _preventNativeRecipientInvalid() {
        this.form?.querySelector('[name="recipientEmail"]')?.addEventListener("invalid", this._boundRecipientInvalid);
      }

      _setLoading(loading) {
        if (!this.submitButton) return;
        if (loading || this._buttonObserver.takeRecords().length) {
          this._purchaseDisabled = this._buttonDisabled();
        }
        this._submitting = loading;
        this.submitButton.setAttribute("aria-busy", String(loading));
        this._applyButtonDisabled();
      }

      _buttonDisabled() {
        return !this.submitButton || this.submitButton.disabled || this.submitButton.getAttribute("aria-disabled") === "true";
      }

      _applyButtonDisabled() {
        // Product detail also updates disabled/aria-disabled as variants change.
        // Keep that business state separate from the temporary submission lock.
        this._buttonObserver.disconnect();
        const disabled = Boolean(this._submitting || this._purchaseDisabled);
        this.submitButton.disabled = disabled;
        this.submitButton.setAttribute("aria-disabled", String(disabled));
        if (this._submitting) {
          this._buttonObserver.observe(this.submitButton, { attributes: true, attributeFilter: ["disabled", "aria-disabled"] });
        }
      }

      _setSuccess() {
        if (!this.submitButton) return;
        this.submitButton.setAttribute("data-added", "true");
        this._setStatus(window.variantStrings?.addedToCart || "Added to cart");
        window.clearTimeout(this._successTimer);
        this._successTimer = window.setTimeout(() => this.submitButton?.removeAttribute("data-added"), 2000);
      }

      _setStatus(message) {
        const status = (this._productDetail || this).querySelector("[data-product-status]");
        if (status) status.textContent = message;
      }

      toggleSubmitButton(disable = true, text, updateBuyNow = true) {
        if (!this.submitButton) return;
        this._purchaseDisabled = disable;
        this._applyButtonDisabled();
        const label = this.submitButton.querySelector("[data-add-to-cart-label]");
        if (label && text != null) {
          label.textContent = text;
        } else if (label && !disable) {
          label.textContent = window.variantStrings?.addToCart || this.submitButton.getAttribute("data-add-text") || label.textContent;
        }
        if (updateBuyNow) {
          // Dawn parity: Shopify's accelerated-checkout button manages its
          // own disabled lifecycle — only Genstore's express button is ours.
          document.querySelectorAll(".express-buy-it-now").forEach((button) => {
            button.toggleAttribute("disabled", disable);
          });
        }
      }

      handleErrorMessage(message = "") {
        if (this.hideErrors) return;
        const wrapper = (this._productDetail || this).querySelector("[data-product-form-error]");
        if (!wrapper) return;
        const messageEl = wrapper.querySelector("[data-product-form-error-message]");
        if (messageEl) messageEl.textContent = message;
        wrapper.hidden = !message;
      }

      _renderCartContents(parsedState) {
        if (this.cart?.renderContents) {
          this.cart.classList?.remove("is-empty");
          try {
            this.cart.renderContents(this._withoutHeaderCartBubbleSection(parsedState));
          } catch (error) {
            console.error(error);
          }
        }
        this._renderCartBubbleSections(parsedState);
      }

      _renderCartBubbleSections(parsedState) {
        const sections = parsedState?.sections;
        if (!sections) return;
        const targets = [
          { section: "ai-cart-icon-bubble", selector: ".ai-cart-bubble-anchor" },
          { section: "cart-icon-bubble", selector: ".cart-count-bubble" },
        ];
        targets.forEach(({ section, selector }) => {
          const html = sections[section];
          if (!html) return;
          const targets = this._getCartBubbleTargets(selector);
          if (!targets.length) return;
          const source = new DOMParser().parseFromString(html, "text/html").querySelector(selector);
          if (source) targets.forEach(target => { target.innerHTML = source.innerHTML; });
        });
      }

      _getCartBubbleTargets(selector) {
        // Attribute-marked header counts are synchronized from cart JSON by
        // product detail; section HTML must preserve their authored structure.
        return Array.from(this.ownerDocument.querySelectorAll(selector)).filter(target => {
          const entry = target.closest('[data-header-action="cart"]') || target;
          return !target.closest('[data-cart-count-bubble]') && !entry.querySelector('[data-cart-count-bubble]');
        });
      }

      _notifyCartErrorToast(payload) {
        const message = payload?.description || payload?.message || "Add to cart failed";
        window.FlyToCart?.showToast?.(message, { type: "error" });
      }

      getSectionsToRender() {
        return [
          {
            id: "ai-cart-icon-bubble",
            section: "ai-cart-icon-bubble",
            selectors: [".ai-cart-bubble-anchor"],
          },
          {
            id: "cart-icon-bubble",
            section: "cart-icon-bubble",
            selectors: [".cart-count-bubble"],
          },
        ].filter(section => this._getCartBubbleTargets(section.selectors[0]).length);
      }

      _getSectionsToRequest() {
        const sections = new Map();
        const addSection = (section) => {
          const sectionId = section?.section || section?.id;
          if (!sectionId || sections.has(sectionId)) return;
          sections.set(sectionId, section);
        };

        (this.cart?.getSectionsToRender?.() || []).forEach(addSection);
        this.getSectionsToRender().forEach(addSection);

        return Array.from(sections.values());
      }

      _withoutHeaderCartBubbleSection(parsedState) {
        if (!parsedState?.sections?.["ai-cart-icon-bubble"]) return parsedState;
        const { ["ai-cart-icon-bubble"]: _headerBubble, ...sections } = parsedState.sections;
        return { ...parsedState, sections };
      }

      _publishCartUpdate(cartData, variantId) {
        const detail = {
          source: "product-form",
          productVariantId: variantId,
          cartData,
        };
        if (window.AIThemeEvents?.publishCartUpdate) {
          window.AIThemeEvents.publishCartUpdate(detail);
        }
        if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, detail);
        }
      }

      _publishCartError(response, variantId) {
        const detail = {
          source: "product-form",
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message || response.description,
        };
        if (window.AIThemeEvents?.publishCartError) {
          window.AIThemeEvents.publishCartError(detail);
        }
        if (typeof publish === "function" && window.PUB_SUB_EVENTS?.cartError) {
          publish(PUB_SUB_EVENTS.cartError, detail);
        }
      }
    },
  );
}
